import React, { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { ShieldAlert, FileCheck, CheckCircle2, XCircle, PenTool, Type, RotateCcw } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { useInAppDialog } from './InAppDialog';

interface FerpaOverrideModalProps {
  onGrant: (signatureData: { type: 'draw' | 'type', value: string, name: string, date: string }) => void;
  onDecline: () => void;
  studentName: string;
}

export const FerpaOverrideModal: React.FC<FerpaOverrideModalProps> = ({ onGrant, onDecline, studentName }) => {
  const { showAlert } = useInAppDialog();
  const [signatureType, setSignatureType] = useState<'draw' | 'type'>('type');
  const [typedName, setTypedName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasPaperConsent, setHasPaperConsent] = useState(false);
  const sigPad = useRef<SignatureCanvas>(null);

  const clearSignature = () => {
    sigPad.current?.clear();
    setIsDrawing(false);
  };

  const handleGrant = () => {
    if (!hasPaperConsent) {
      showAlert({
        title: "Consent File Verification",
        message: "Please confirm you have a paper or digital copy of the consent on file.",
        type: "warning"
      });
      return;
    }

    const date = new Date().toISOString();
    if (signatureType === 'type') {
      if (!typedName.trim()) {
        showAlert({
          title: "Name Required",
          message: "Please type your full legal name to sign.",
          type: "warning"
        });
        return;
      }
      onGrant({ type: 'type', value: typedName, name: typedName, date });
    } else {
      if (sigPad.current?.isEmpty()) {
        showAlert({
          title: "Signature Required",
          message: "Please provide a signature on the pad.",
          type: "warning"
        });
        return;
      }
      const signatureValue = sigPad.current?.getCanvas().toDataURL('image/png') || '';
      onGrant({ type: 'draw', value: signatureValue, name: typedName || 'Digital Signature', date });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
      
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-rose-600 p-6 text-white shrink-0">
          <div className="flex items-center space-x-3 mb-2">
            <ShieldAlert className="w-6 h-6 text-white" />
            <h2 className="text-xl font-black uppercase tracking-tight">FERPA Consent Override</h2>
          </div>
          <p className="text-white/80 text-xs font-bold uppercase tracking-widest">
            Administrator Override for Student: {studentName}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-start space-x-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={hasPaperConsent} onChange={(e) => setHasPaperConsent(e.target.checked)} className="mt-1 w-4 h-4 text-rose-600 border-rose-300 rounded focus:ring-rose-500" />
              <span className="text-[11px] text-rose-900 font-bold leading-relaxed">
                I confirm that I have a paper or digital copy of the FERPA consent on file for this student, and I am authorized to override this requirement.
              </span>
            </label>
          </div>

          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Administrator Signature</span>
                <p className="text-[11px] text-slate-600 font-medium">Sign as yourself.</p>
              </div>
              <div className="flex bg-white rounded-lg border border-slate-200 p-1">
                <button onClick={() => setSignatureType('type')} className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${signatureType === 'type' ? 'bg-rose-600 text-white' : 'text-slate-400 hover:text-slate-600'}`}>
                  <Type className="w-3 h-3" /> Type
                </button>
                <button onClick={() => setSignatureType('draw')} className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${signatureType === 'draw' ? 'bg-rose-600 text-white' : 'text-slate-400 hover:text-slate-600'}`}>
                  <PenTool className="w-3 h-3" /> Draw
                </button>
              </div>
            </div>

            {signatureType === 'type' ? (
              <input type="text" placeholder="Type your full legal name" value={typedName} onChange={(e) => setTypedName(e.target.value)} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:ring-2 focus:ring-rose-500 outline-none transition-all placeholder:text-slate-300" />
            ) : (
              <div className="relative h-32 bg-white border border-slate-200 rounded-xl overflow-hidden group">
                <SignatureCanvas ref={sigPad} penColor="black" canvasProps={{ className: 'w-full h-full cursor-crosshair' }} onBegin={() => setIsDrawing(true)} />
                {!isDrawing && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><p className="text-[10px] font-black uppercase tracking-widest text-slate-300">Sign Here</p></div>}
                <button onClick={clearSignature} className="absolute bottom-2 right-2 p-1.5 bg-slate-100 text-slate-400 rounded-md hover:bg-slate-200 hover:text-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-200 flex flex-col md:flex-row items-center justify-center gap-4 shrink-0">
          <button onClick={onDecline} className="w-full md:w-auto px-8 py-3 rounded-xl border border-slate-200 text-slate-500 font-black uppercase tracking-widest text-xs hover:bg-white transition-all">Cancel</button>
          <button onClick={handleGrant} className="w-full md:w-auto px-10 py-3 rounded-xl bg-rose-600 text-white font-black uppercase tracking-widest text-xs hover:bg-rose-700 shadow-lg shadow-rose-600/20 transition-all">Override Consent</button>
        </div>
      </motion.div>
    </div>
  );
};
